<template>
    <div class="mini-cal">
        <div class="cal-month-nav">
            <v-btn icon x-small class="cal-arrow" @click="changeMonth(-1)">
                <v-icon size="18">mdi-chevron-left</v-icon>
            </v-btn>
            <span class="cal-month-name">{{ monthName }}</span>
            <v-btn icon x-small class="cal-arrow" @click="changeMonth(1)">
                <v-icon size="18">mdi-chevron-right</v-icon>
            </v-btn>
        </div>
        <div class="cal-days-header">
            <div v-for="(label, i) in weekDays" :key="i" class="cal-day-name">{{ label }}</div>
        </div>
        <div class="cal-dates">
            <div v-for="(cell, i) in cells" :key="i"
                :class="['cal-cell', { today: cell && isToday(cell.date), selected: cell && isSelected(cell.date) && !isToday(cell.date), empty: !cell }]"
                @click="cell && $emit('select', cell.date)">
                <template v-if="cell">
                    <div v-if="isToday(cell.date)" class="cal-today-circle" />
                    <div v-if="isSelected(cell.date)" class="cal-sel-ring" />
                    <span>{{ cell.day }}</span>
                    <div v-if="hasEvent(cell.date)" class="cal-dot" />
                </template>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "AgendaMiniCalendar",
    props: {
        value: {
            type: String,
            default: null,
        },
        eventDates: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            displayMonth: this.$moment(this.value || undefined).startOf('month'),
        }
    },
    computed: {
        today(vm = this) {
            return vm.$moment().format('YYYY-MM-DD')
        },
        monthName(vm = this) {
            return vm.displayMonth.format('MMMM YYYY')
        },
        weekDays(vm = this) {
            const start = vm.$moment().startOf('isoWeek')
            return Array.from({ length: 7 }, (_, i) => vm.weekdayLetter(start.clone().add(i, 'days')))
        },
        eventDatesSet(vm = this) {
            return new Set(vm.eventDates)
        },
        cells(vm = this) {
            const startOfMonth = vm.displayMonth.clone().startOf('month')
            const daysInMonth = vm.displayMonth.daysInMonth()
            const leading = startOfMonth.isoWeekday() - 1
            const arr = []
            for (let i = 0; i < leading; i++) arr.push(null)
            for (let d = 1; d <= daysInMonth; d++) {
                arr.push({ day: d, date: startOfMonth.clone().date(d).format('YYYY-MM-DD') })
            }
            while (arr.length % 7 !== 0) arr.push(null)
            return arr
        },
    },
    methods: {
        changeMonth(delta) {
            const vm = this
            vm.displayMonth = vm.displayMonth.clone().add(delta, 'month')
            vm.$emit('month-change', vm.displayMonth.format('YYYY-MM-DD'))
        },
        isToday(date) {
            return date === this.today
        },
        isSelected(date) {
            return date === this.value
        },
        hasEvent(date) {
            return this.eventDatesSet.has(date)
        },
    },
    watch: {
        value(val) {
            if (val) this.displayMonth = this.$moment(val).startOf('month')
        },
    },
}
</script>

<style scoped>
.mini-cal {
    padding: 16px 20px;
}

.cal-month-nav {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 12px;
}

.cal-month-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    min-width: 90px;
    text-align: center;
}

.cal-arrow {
    width: 22px !important;
    height: 22px !important;
    border-radius: 5px !important;
    transition: background 0.15s;
}

.cal-arrow >>> .v-icon {
    color: var(--v-txtMuted-base);
}

.cal-arrow:hover >>> .v-icon {
    color: var(--v-txtSecondary-base);
}

.cal-arrow:hover {
    background: var(--v-surface2-base) !important;
}

.cal-days-header {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 4px;
}

.cal-day-name {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    text-align: center;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.cal-dates {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
}

.cal-cell {
    width: 30px;
    height: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: var(--v-txtSecondary-base);
    position: relative;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.12s;
    border-radius: 50%;
}

.cal-cell.empty {
    cursor: default;
}

.cal-cell:not(.empty):not(.today):hover {
    background: var(--v-surface2-base);
}

.cal-cell.selected {
    color: var(--v-txtPrimary-base);
    font-weight: 600;
}

.cal-cell.today {
    font-weight: 700;
    color: #0C0E12;
}

.cal-today-circle {
    position: absolute;
    inset: 2px;
    border-radius: 50%;
    background: var(--brand-gradient);
    z-index: 0;
}

.cal-sel-ring {
    position: absolute;
    inset: 0;
    border: 2px solid var(--v-accent-base);
    border-radius: 50%;
    z-index: 0;
    pointer-events: none;
}

.cal-cell span {
    position: relative;
    z-index: 1;
}

.cal-dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: var(--v-accent-base);
    position: absolute;
    bottom: 2px;
}

.cal-cell.today .cal-dot {
    background: #0C0E12;
}
</style>
